import React, { useState, useEffect } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// SETUP: Create a free form at https://formspree.io
// Replace the ID below with your form's endpoint ID (e.g., 'xyzabcde')
// Submissions will be emailed to whatever address you registered with.
// ═══════════════════════════════════════════════════════════════════════════════
const FORMSPREE_ID = 'mjgakrqq'; // ← Replace this

const METRICS = [
  'LPI (Logistics Performance)',
  'Water Access %',
  'Sanitation Access %',
  'Electricity Access %',
  'Internet Penetration %',
  'Education (Tertiary Enrollment %)',
  'Bank Account Penetration %',
  'CAPCI (Credit-Augmented PCI)',
  'GCR (Grassroots Credit Ratio)',
  'Rule of Law',
  'Real Wage Growth',
  'Insurance Penetration',
  'Control of Corruption',
  'Physicians per 1,000',
  'GDP Per Capita',
  'GNI Per Capita',
  'Government Debt % GDP',
  'Household Debt',
  'DTI (Debt-to-Income %)',
  'Other (specify in details)',
];

const YEARS = Array.from({ length: 26 }, (_, i) => String(2000 + i));

const sty = {
  page: { maxWidth: 680, margin: '0 auto', padding: '2rem 1.5rem 4rem' },
  h1: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '2rem', color: '#0f172a', marginBottom: '0.5rem' },
  intro: { color: '#475569', lineHeight: 1.8, marginBottom: '2rem', fontSize: '0.95rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '1.25rem' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '0.7rem 1rem', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', color: '#1e293b', background: '#fff', width: '100%', boxSizing: 'border-box' },
  select: { padding: '0.7rem 1rem', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', color: '#1e293b', background: '#fff', width: '100%', boxSizing: 'border-box' },
  textarea: { padding: '0.7rem 1rem', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', color: '#1e293b', background: '#fff', width: '100%', boxSizing: 'border-box', minHeight: 100, fontFamily: 'inherit', resize: 'vertical' },
  row: { display: 'flex', gap: '1rem' },
  half: { flex: 1, display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  btn: { padding: '0.85rem 2rem', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem', transition: 'all 0.15s' },
  honeypot: { position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden', tabIndex: -1 },
  note: { background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: '1rem 1.2rem', fontSize: '0.88rem', color: '#0f766e', lineHeight: 1.7 },
  success: { textAlign: 'center', padding: '3rem 2rem' },
  successIcon: { fontSize: '3rem', marginBottom: '1rem' },
  error: { color: '#dc2626', fontSize: '0.88rem', marginTop: '0.25rem' },
};

export default function SuggestDataPage() {
  const [form, setForm] = useState({
    name: '', email: '', country: '', year: '', metric: '',
    currentValue: '', suggestedValue: '', source: '', details: '',
    _honeypot: '', // spam trap
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/countries.json`)
      .then(r => r.json())
      .then(setCountries)
      .catch(() => {});
  }, []);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Honeypot check
    if (form._honeypot) return;

    // Validation
    if (!form.name.trim() || !form.email.trim()) {
      setError('Please provide your name and email.');
      return;
    }
    if (!form.country || !form.year || !form.metric) {
      setError('Please select a country, year, and metric.');
      return;
    }
    if (!form.suggestedValue.trim()) {
      setError('Please enter a suggested value.');
      return;
    }
    if (!form.source.trim()) {
      setError('Please cite your data source — this helps us verify the suggestion.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          country: form.country,
          year: form.year,
          metric: form.metric,
          current_value: form.currentValue || '(not specified)',
          suggested_value: form.suggestedValue,
          source: form.source,
          details: form.details || '(none)',
          _subject: `Data suggestion: ${form.country} ${form.year} ${form.metric}`,
        }),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Submission failed. Please try again or email your suggestion directly.');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={sty.page}>
        <div style={sty.success}>
          <div style={sty.successIcon}>✓</div>
          <h2 style={{ ...sty.h1, fontSize: '1.6rem' }}>Thank you for your suggestion</h2>
          <p style={{ color: '#64748b', marginTop: '0.75rem', lineHeight: 1.7 }}>
            Your data suggestion has been submitted for review. We verify all suggestions
            against authoritative sources before incorporating them into the index.
          </p>
          <button
            onClick={() => { setSubmitted(false); setForm({ name: form.name, email: form.email, country: '', year: '', metric: '', currentValue: '', suggestedValue: '', source: '', details: '', _honeypot: '' }); }}
            style={{ ...sty.btn, background: '#0f766e', color: '#fff', marginTop: '1.5rem' }}
          >
            Submit another suggestion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={sty.page}>
      <h1 style={sty.h1}>Suggest a Data Correction</h1>
      <p style={sty.intro}>
        The Macro-Governance Index relies on publicly available data from international organizations.
        If you've found an error or have access to more recent data for a specific country and metric,
        we welcome your input. All suggestions are reviewed and verified against authoritative sources
        before being incorporated.
      </p>

      <div style={sty.note}>
        <strong>Please cite your source.</strong> We can only accept suggestions backed by verifiable,
        publicly available data — government statistics, World Bank, UNESCO, WHO, central bank reports,
        or peer-reviewed publications.
      </div>

      <div style={{ height: '1.5rem' }} />

      <form onSubmit={handleSubmit} style={sty.form}>
        {/* Honeypot — hidden from humans, filled by bots */}
        <div style={sty.honeypot} aria-hidden="true">
          <label htmlFor="_honeypot">Do not fill this field</label>
          <input id="_honeypot" name="_honeypot" type="text" value={form._honeypot} onChange={update('_honeypot')} autoComplete="off" />
        </div>

        <div style={sty.row}>
          <div style={sty.half}>
            <label style={sty.label}>Your Name *</label>
            <input style={sty.input} type="text" value={form.name} onChange={update('name')} placeholder="Full name" />
          </div>
          <div style={sty.half}>
            <label style={sty.label}>Your Email *</label>
            <input style={sty.input} type="email" value={form.email} onChange={update('email')} placeholder="email@example.com" />
          </div>
        </div>

        <div style={sty.row}>
          <div style={sty.half}>
            <label style={sty.label}>Country *</label>
            <select style={sty.select} value={form.country} onChange={update('country')}>
              <option value="">Select country...</option>
              {countries.map(c => <option key={c.code} value={`${c.code} - ${c.name}`}>{c.name}</option>)}
            </select>
          </div>
          <div style={sty.half}>
            <label style={sty.label}>Year *</label>
            <select style={sty.select} value={form.year} onChange={update('year')}>
              <option value="">Select year...</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div style={sty.fieldGroup}>
          <label style={sty.label}>Metric *</label>
          <select style={sty.select} value={form.metric} onChange={update('metric')}>
            <option value="">Select metric...</option>
            {METRICS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div style={sty.row}>
          <div style={sty.half}>
            <label style={sty.label}>Current Value (if known)</label>
            <input style={sty.input} type="text" value={form.currentValue} onChange={update('currentValue')} placeholder="e.g., 93.31" />
          </div>
          <div style={sty.half}>
            <label style={sty.label}>Suggested Value *</label>
            <input style={sty.input} type="text" value={form.suggestedValue} onChange={update('suggestedValue')} placeholder="e.g., 94.58" />
          </div>
        </div>

        <div style={sty.fieldGroup}>
          <label style={sty.label}>Data Source / Citation *</label>
          <input style={sty.input} type="text" value={form.source} onChange={update('source')} placeholder="e.g., UNESCO GER.1T8, World Bank WDI SE.TER.ENRR" />
        </div>

        <div style={sty.fieldGroup}>
          <label style={sty.label}>Additional Details</label>
          <textarea style={sty.textarea} value={form.details} onChange={update('details')} placeholder="Any context, links to source documents, or explanation of the correction..." />
        </div>

        {error && <p style={sty.error}>{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          style={{
            ...sty.btn,
            background: submitting ? '#94a3b8' : '#0f766e',
            color: '#fff',
            alignSelf: 'flex-start',
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Suggestion'}
        </button>
      </form>
    </div>
  );
}
